import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";

import { font, welcomeScreenUI } from "../../configs";
import { GENDERS } from "../../constants/modal";
import { capitalizeFirstLetter } from "../../utils";
import { findBrandByDomain } from "../../configs/configLoader";

import "./index.css";

interface IPropsStepGenderSelect {
  selectedGender: string;
  setSelectedGender: React.Dispatch<React.SetStateAction<string>>;
  nextStep: () => void;
  descriptionStyles: {
    color: string;
    fontSize: React.CSSProperties["fontSize"];
    fontWeight: number;
    textAlign: React.CSSProperties["textAlign"];
    textTransform: React.CSSProperties["textTransform"];
    justifyContent: React.CSSProperties["justifyContent"];
    fontFamily: string;
    lineHeight: string;
    margin: string;
  };
}

type GenderButtonType = "focused" | "unfocused";

const StepGenderSelect: FC<IPropsStepGenderSelect> = ({
  selectedGender,
  setSelectedGender,
  nextStep,
  descriptionStyles,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const { t } = useTranslation("components/intro");

  const brandDefined = findBrandByDomain();

  const getButtonsStyles = (type: GenderButtonType) => {
    const defaultButtonsStyles = {
      backgroundColor: welcomeScreenUI.genderCTA[type].backgroundColor,
      borderRadius: welcomeScreenUI.genderCTA[type].borderRadius,
      borderColor: welcomeScreenUI.genderCTA[type].borderColor,
      borderWidth: welcomeScreenUI.genderCTA[type].borderWidth,
      fontSize: welcomeScreenUI.genderCTA[type].fontSize,
      fontWeight: welcomeScreenUI.genderCTA[type].fontWeight,
      textTransform:
        brandDefined?.name === "Lacoste" && isMobile
          ? "uppercase"
          : (welcomeScreenUI.genderCTA[type]
              .textTransform as React.CSSProperties["textTransform"]),
      color: welcomeScreenUI.genderCTA[type].fontColor,
      fontFamily: isMobile
        ? `${font}, sans-serif`
        : `${font}, sans-serif !important`,
      letterSpacing: (welcomeScreenUI.genderCTA[type] as any)?.letterSpacing,
    };

    return defaultButtonsStyles;
  };

  const femaleButtonStyles =
    selectedGender === GENDERS.F
      ? getButtonsStyles("focused")
      : getButtonsStyles("unfocused");

  const maleButtonStyles =
    selectedGender === GENDERS.M
      ? getButtonsStyles("focused")
      : getButtonsStyles("unfocused");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();

    if (e.key === "Tab") {
      e.preventDefault();

      const selectGender = !selectedGender
        ? GENDERS.F
        : selectedGender === GENDERS.F
        ? GENDERS.M
        : GENDERS.M;

      setSelectedGender(selectGender);

      if (selectedGender === GENDERS.M) {
        const heightInput = document.querySelector(
          "#height"
        ) as HTMLInputElement | null;
        if (heightInput) {
          heightInput?.focus();
        } else {
          // logger.log("Element with id 'height' not found.");
        }
      }
    }
  };

  const handleFocus = () => {
    setSelectedGender(GENDERS.F);
  };

  const genderMaleKey = "gender.male";
  const genderFemaleKey = "gender.female";

  return (
    <div>
      <p
        className="breasts__titles__title"
        style={{
          ...descriptionStyles,
          textTransform: "none",
          marginBottom: "30px",
        }}
      >
        {t("gender.description")}
      </p>
      <div
        className="gender-buttons"
        style={{
          marginBottom: "30px",
        }}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        tabIndex={0}
      >
        <button
          type="button"
          className={selectedGender === GENDERS.F ? "selected" : ""}
          style={{
            ...femaleButtonStyles,
            textTransform:
              femaleButtonStyles.textTransform === "capitalize"
                ? "none"
                : femaleButtonStyles.textTransform,
          }}
          onClick={() => {
            setSelectedGender(GENDERS.F);
            if (isMobile) nextStep();
          }}
        >
          {femaleButtonStyles.textTransform === "capitalize"
            ? capitalizeFirstLetter(t(genderFemaleKey))
            : t(genderFemaleKey)}
        </button>
        <button
          type="button"
          className={selectedGender === GENDERS.M ? "selected" : ""}
          style={{
            ...maleButtonStyles,
            textTransform:
              maleButtonStyles.textTransform === "capitalize"
                ? "none"
                : maleButtonStyles.textTransform,
          }}
          onClick={() => {
            setSelectedGender(GENDERS.M);
            if (isMobile) nextStep();
          }}
        >
          {maleButtonStyles.textTransform === "capitalize"
            ? capitalizeFirstLetter(t(genderMaleKey))
            : t(genderMaleKey)}
        </button>
      </div>
    </div>
  );
};

export default StepGenderSelect;
